import { createContext, useContext } from "react";

export interface PortalNavigateOptions {
  replace?: boolean;
}

/** Navigation for client pages: a client path plus react-router's `replace`. */
export type PortalNavigate = (to: string, options?: PortalNavigateOptions) => void;

/**
 * Navigation override for client pages rendered inside another shell.
 *
 * The consultant read-through (/consultant/clients/:orgId/portal/*) provides a
 * function that prefixes client paths ("/documents") with the portal route and
 * navigates there, honouring `replace`. Outside the read-through the context is
 * null and pages fall back to react-router - see hooks/usePortalNavigate.ts,
 * which every client page should use instead of calling useNavigate() directly.
 */
export const PortalNavContext = createContext<PortalNavigate | null>(null);

export function usePortalNav(): PortalNavigate | null {
  return useContext(PortalNavContext);
}

/**
 * The route prefix of the shell a client page is rendered in ("" at the root,
 * "/consultant/clients/:orgId/portal" in the read-through). Rendered links
 * (components/PortalLink.tsx) resolve through it, so an <a href> stays inside
 * the portal just as usePortalNavigate() does.
 */
export const PortalPrefixContext = createContext<string>("");

export function usePortalPrefix(): string {
  return useContext(PortalPrefixContext);
}

/** A client path ("/documents") resolved against the current shell's prefix. */
export function usePortalPath(path: string): string {
  return prefixPortalPath(usePortalPrefix(), path);
}

/** Prefixes a client path with a portal route, keeping any query string. */
export function prefixPortalPath(prefix: string, path: string): string {
  const base = prefix.replace(/\/+$/, "");
  if (!base) return path;
  if (path === "" || path === "/") return base;
  return base + (path.startsWith("/") ? path : `/${path}`);
}
